// ADR 28.9.26d §4 — a native `<dialog>`, shown modal, kept controlled.
//
// `showModal()` puts the element in the top layer and makes the rest of the
// page inert, so there is no focus trap to write, and Escape fires `cancel`, so
// `useEscapeCloses` is not needed. What the browser does not settle, this does,
// all in **one passive effect** — so StrictMode's mount-unmount-mount records
// the trigger again on its second run rather than the dialog's own first
// action, and the cleanup runs after React's DOM mutations, when a trigger
// re-enabled in the same commit is focusable again:
//
// 1. record the focused element, then `showModal()` guarded by `!open` (some
//    engines still throw `InvalidStateError` on a second show);
// 2. `cancel` → `preventDefault()` + `onClose()`, so the caller decides;
// 3. a `close` the browser fires on its own (Chromium's close-watcher lets a
//    second Escape close without a cancelable `cancel`) → `onClose()`, or the
//    dialog would vanish while React still renders it. ADR 16.10.26p §1 — only
//    while `!open`: a browser's own close clears `open` before it dispatches
//    `close`, but `close()` *queues* its event, so StrictMode's cleanup event
//    arrives after the remount's `showModal()` (Safari), with `open` true again,
//    and must not unmount the dialog it just reopened;
// 4. a click whose target is the `<dialog>` itself — the backdrop, since the
//    padding lives on an inner wrapper — → `onClose()`, as `AlertDialog`
//    dismisses on an outside tap;
// 5. cleanup: listeners off *first*, so its own `close()` does not call
//    `onClose` a second time, then re-focus the recorded element if it is
//    still connected.
//
// A caller renders the dialog only while it is open; unmounting is closing.
// A sheet built later needs only its own classes on top of this.

import { useEffect, useRef, type RefObject } from 'react'

export function useModalDialog(
  onClose: () => void,
  onOpen?: (dialog: HTMLDialogElement) => void,
): RefObject<HTMLDialogElement> {
  const ref = useRef<HTMLDialogElement>(null)
  // The latest callbacks, read at event time, so the effect runs once per mount.
  const latest = useRef({ onClose, onOpen })
  latest.current = { onClose, onOpen }

  useEffect(() => {
    const dialog = ref.current
    if (dialog === null) return
    const opener = document.activeElement
    if (!dialog.open) dialog.showModal()
    latest.current.onOpen?.(dialog)

    const onCancel = (event: Event) => {
      event.preventDefault()
      latest.current.onClose()
    }
    const onBrowserClose = () => {
      if (!dialog.open) latest.current.onClose()
    }
    const onClick = (event: MouseEvent) => {
      if (event.target === dialog) latest.current.onClose()
    }
    dialog.addEventListener('cancel', onCancel)
    dialog.addEventListener('close', onBrowserClose)
    dialog.addEventListener('click', onClick)
    return () => {
      dialog.removeEventListener('cancel', onCancel)
      dialog.removeEventListener('close', onBrowserClose)
      dialog.removeEventListener('click', onClick)
      if (dialog.open) dialog.close()
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [])

  return ref
}
